import { Space } from 'antd';
import classNames from 'classnames';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useDebouncedCallback } from 'use-debounce';

import { Input } from 'ui/base';

const DEFAULT_DEBOUNCE = 1000; // default debounce time in ms

/**
 * @typedef {{
 *  autofocus?:boolean,
 *  onSearch?: (value:string) => void,
 *  defaultValue?:string,
 *  placeholder?:string,
 *  debounce?:number,
 *  minCharacters?: number,
 * } & import('antd/lib/input').SearchProps} FilterFieldProps
 */

/** @type {React.FC<FilterFieldProps>} */
const SearchInput = React.forwardRef(({
  className,
  onSearch,
  defaultValue,
  minCharacters,
  placeholder = 'page.home.filters.search',
  debounce = DEFAULT_DEBOUNCE,
  ...rest
}, ref) => {
  const intl = useIntl();

  const [displayError, setDisplayError] = React.useState(false);

  const debouncedFunction = useDebouncedCallback((newText) => {
    const hasError = minCharacters && newText.length > 0 && newText.length < minCharacters;
    setDisplayError(hasError);
    if (!hasError) {
      onSearch(newText);
    }
  }, debounce);

  const handleChange = React.useCallback(
    ({ target }) => debouncedFunction(target.value),
    [debouncedFunction]
  );

  return (
    <div className={classNames('d-flex flex-column input-search-container', className)}>
      <Input.Search
        ref={ref}
        data-cy="filter-search"
        onChange={handleChange}
        defaultValue={defaultValue}
        placeholder={intl.formatMessage({ id: placeholder, defaultMessage: 'Search' })}
        status={displayError ? 'error' : undefined}
        suffix={
          <Space className={classNames({ 'text-danger': displayError })}>
            {displayError && (
              <FormattedMessage
                id="SearchField.error.minLength"
                defaultMessage="{minLength} characters min."
                values={{ minLength: minCharacters }}
              />
            )}
            <span className="fb-icon--search" />
          </Space>
        }
        {...rest}
      />
    </div>
  );
});

export default SearchInput;
